import { useMemo, useState } from 'react';
import type { IngestionRun } from '@clarity/contract';
import { useIngestionRuns, useTriggerIngestion, useRetryGather } from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { QueryState } from '../QueryState.js';
import { dateTime, num, period } from '../format.js';
import { IconIngestion } from '../../components/icons.js';

type Mode = 'daily' | 'backfill';

/**
 * Admin-only ingestion operations panel (admin-ingestion-control Req 5;
 * onboarding-automation retry UX).
 *
 * Lists recent per-store ingestion runs newest-first as a RESPONSIVE CARD GRID
 * (not a wide table), so it reads cleanly on a phone as well as a desktop. An
 * admin can trigger a batch — all enabled stores, one store (per-card "Retry"),
 * or **every failed store at once** ("Retry all failed") — in `daily`
 * (incremental) or `backfill` (full-window) mode. The trigger is asynchronous
 * (`202 { batchId, storeCount }`); {@link useIngestionRuns} polls the history
 * until the new `running` rows finalize, so the grid reflects progress without a
 * manual refresh. The server's stale-run reaper flips any stranded `running` row
 * to `failed` on each read, so a crashed/hung gather surfaces as a retryable
 * failure rather than a permanent "Running".
 *
 * Admin-only: the route guard in `AppRoutes` is the real gate; the server
 * enforces admin-only on both routes (Req 2).
 */
export function IngestionRuns() {
  const { t } = useI18n();
  const runs = useIngestionRuns();
  const trigger = useTriggerIngestion();
  // Re-running a failed onboarding GATHER (`reference` run) goes through the
  // gather edge, NOT the admin daily/backfill trigger — a gather runs before a
  // store is enabled, so the daily trigger would just skip it ("store not
  // enabled"). This mutation re-fires the gather for a specific store.
  const retryGather = useRetryGather();
  // One shared mode choice drives "Run all" and the daily/backfill retries (Req
  // 5.2). It does NOT apply to a gather retry — a gather is always the reference
  // window.
  const [mode, setMode] = useState<Mode>('daily');

  const rows: IngestionRun[] = runs.data?.status === 200 ? runs.data.body : [];

  const anyRetryPending = trigger.isPending || retryGather.isPending;

  // COLLAPSE to one card per store (the LATEST run), so a retry — which appends
  // a new `ingestion_runs` row (the history is append-only) — replaces the prior
  // card instead of stacking a second one for the same store. The full history
  // stays in the DB; the panel shows the CURRENT state per store. `rows` are
  // already newest-first (the repo orders by createdAt desc), so the first row
  // seen for a store is its latest. A `running` retry naturally supersedes the
  // old failed card here too.
  const latestPerStore = useMemo(() => {
    const seen = new Set<string>();
    const out: IngestionRun[] = [];
    for (const r of rows) {
      if (seen.has(r.storeId)) continue;
      seen.add(r.storeId);
      out.push(r);
    }
    return out;
  }, [rows]);

  // At-a-glance counts for the header summary chips — over the collapsed view so
  // they match the cards shown (one per store).
  const counts = useMemo(() => {
    let running = 0;
    let failed = 0;
    let succeeded = 0;
    for (const r of latestPerStore) {
      if (r.status === 'running') running += 1;
      else if (r.status === 'failed') failed += 1;
      else if (r.status === 'succeeded') succeeded += 1;
    }
    return { running, failed, succeeded };
  }, [latestPerStore]);

  // The distinct stores whose LATEST run failed, SPLIT by how they must be
  // retried: a failed `reference` (onboarding gather) re-runs through the gather
  // edge; a failed `daily`/`backfill` re-runs through the admin trigger. Keyed
  // by store so a store with several failed rows is retried once (its latest
  // failed row — rows are newest-first — decides the path).
  const failed = useMemo(() => {
    const gatherStoreIds = new Set<string>();
    const triggerStoreIds = new Set<string>();
    const seen = new Set<string>();
    for (const r of rows) {
      if (r.status !== 'failed' || seen.has(r.storeId)) continue;
      seen.add(r.storeId);
      if (r.mode === 'reference') gatherStoreIds.add(r.storeId);
      else triggerStoreIds.add(r.storeId);
    }
    return {
      gatherStoreIds: [...gatherStoreIds],
      triggerStoreIds: [...triggerStoreIds],
      total: gatherStoreIds.size + triggerStoreIds.size,
    };
  }, [rows]);

  function runAll() {
    trigger.mutate({ body: { mode } });
  }

  // Per-card retry: route by the run's mode. A `reference` run re-gathers (works
  // on a not-yet-enabled store); anything else re-triggers daily/backfill.
  function retry(run: IngestionRun) {
    if (run.mode === 'reference') {
      retryGather.mutate({ params: { id: run.storeId }, body: {} });
    } else {
      trigger.mutate({ body: { storeIds: [run.storeId], mode } });
    }
  }

  function retryAllFailed() {
    if (failed.total === 0) return;
    // Re-gather each failed onboarding gather (per-store, the gather edge)...
    for (const storeId of failed.gatherStoreIds) {
      retryGather.mutate({ params: { id: storeId }, body: {} });
    }
    // ...and batch the failed daily/backfill runs through the admin trigger.
    if (failed.triggerStoreIds.length > 0) {
      trigger.mutate({ body: { storeIds: failed.triggerStoreIds, mode } });
    }
  }

  // Surface the accepted batch so the admin sees the trigger landed before the
  // first poll paints the new rows.
  const accepted = trigger.data?.status === 202 ? trigger.data.body : null;

  return (
    <section className="ingestion">
      <div className="page-head">
        <div>
          <h2>{t('ingestion.title')}</h2>
          <p className="sub">{t('ingestion.sub')}</p>
        </div>
      </div>

      <div className="card ingestion-bar" aria-label={t('ingestion.runAll')}>
        <fieldset className="ingestion-mode">
          <legend>{t('ingestion.mode.label')}</legend>
          <div className="segmented" role="radiogroup" aria-label={t('ingestion.mode.label')}>
            <ModeOption value="daily" current={mode} onPick={setMode} label={t('ingestion.mode.daily')} />
            <ModeOption
              value="backfill"
              current={mode}
              onPick={setMode}
              label={t('ingestion.mode.backfill')}
            />
          </div>
          <p className="muted ingestion-mode__hint">
            {mode === 'daily' ? t('ingestion.mode.daily.hint') : t('ingestion.mode.backfill.hint')}
          </p>
        </fieldset>

        <div className="ingestion-bar__actions">
          {failed.total > 0 && (
            <button
              type="button"
              className="danger ingestion-bar__retry-all"
              onClick={retryAllFailed}
              disabled={anyRetryPending}
            >
              {anyRetryPending
                ? t('ingestion.retryAllFailed.pending')
                : t('ingestion.retryAllFailed.count', { count: num(failed.total) })}
            </button>
          )}
          <button type="button" className="primary" onClick={runAll} disabled={trigger.isPending}>
            {trigger.isPending ? t('ingestion.runAll.pending') : t('ingestion.runAll')}
          </button>
        </div>
      </div>

      {(accepted || trigger.isError || retryGather.isError) && (
        <div className="ingestion-bar__notice" aria-live="polite">
          {accepted && !trigger.isPending && (
            <p role="status" className="muted">
              {t(accepted.storeCount === 1 ? 'ingestion.triggered.one' : 'ingestion.triggered.many', {
                count: num(accepted.storeCount),
                batch: accepted.batchId.slice(0, 8),
              })}
            </p>
          )}
          {(trigger.isError || retryGather.isError) && (
            <p role="alert" className="error">
              {t('ingestion.triggerError')}
            </p>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="ingestion-summary" aria-hidden="true">
          {counts.running > 0 && (
            <span className="ingestion-chip ingestion-chip--running">
              {t('ingestion.summary.running', { count: num(counts.running) })}
            </span>
          )}
          {counts.failed > 0 && (
            <span className="ingestion-chip ingestion-chip--failed">
              {t('ingestion.summary.failed', { count: num(counts.failed) })}
            </span>
          )}
          {counts.succeeded > 0 && (
            <span className="ingestion-chip ingestion-chip--succeeded">
              {t('ingestion.summary.succeeded', { count: num(counts.succeeded) })}
            </span>
          )}
        </div>
      )}

      <QueryState isLoading={runs.isLoading} isError={runs.isError} label={t('ingestion.title')}>
        {rows.length === 0 ? (
          <div className="card empty">
            <IconIngestion className="empty__glyph" />
            <p>{t('ingestion.empty')}</p>
          </div>
        ) : (
          <ul className="ingestion-grid" aria-label={t('ingestion.title')}>
            {rows.map((run) => (
              <RunCard key={run.id} run={run} onRetry={retry} pending={anyRetryPending} />
            ))}
          </ul>
        )}
      </QueryState>
    </section>
  );
}

/** One mode choice in the segmented control (a styled radio). */
function ModeOption({
  value,
  current,
  onPick,
  label,
}: {
  value: Mode;
  current: Mode;
  onPick: (m: Mode) => void;
  label: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={active ? 'segmented__opt is-active' : 'segmented__opt'}
      onClick={() => onPick(value)}
    >
      {label}
    </button>
  );
}

/**
 * One run as a self-contained CARD. The header carries the store, the status
 * badge, and (for a finished run) a Retry button; a `running` card pulses its
 * badge. The body is a compact meta grid (started / covered / trigger / mode)
 * plus — when anything was written — the persisted-count chips, and, on a
 * failure, the plain error + failed sources. Reflows to a single column on a
 * phone by construction (the grid is `auto-fit`, the meta `minmax`).
 */
function RunCard({
  run,
  onRetry,
  pending,
}: {
  run: IngestionRun;
  onRetry: (run: IngestionRun) => void;
  pending: boolean;
}) {
  const { t } = useI18n();
  const isRunning = run.status === 'running';
  const when = isRunning ? run.startedAt : (run.finishedAt ?? run.startedAt);
  const p = run.persisted;

  // Retry is offered for any FINISHED run (failed / skipped / even a succeeded
  // one an admin wants to re-pull); a running run has nothing to retry yet.
  const canRetry = !isRunning;

  const persistedParts: { value: number; label: string }[] = [
    { value: p.orders, label: t('ingestion.persisted.orders') },
    { value: p.statements, label: t('ingestion.persisted.statements') },
    { value: p.payouts, label: t('ingestion.persisted.payouts') },
    { value: p.provisionedItems, label: t('ingestion.persisted.provisioned') },
    { value: p.campaigns, label: t('ingestion.persisted.campaigns') },
    { value: p.ordersMatched, label: t('ingestion.persisted.matched') },
    { value: p.rawJsonCaptured, label: t('ingestion.persisted.rawJson') },
  ];
  const anyPersisted = persistedParts.some((part) => part.value > 0);

  return (
    <li className={`ingestion-card ingestion-card--${run.status}`}>
      <header className="ingestion-card__head">
        <div className="ingestion-card__title">
          <div className="ingestion-card__tags">
            <StatusBadge status={run.status} />
            <PlatformBadge platformKey={run.platformKey} />
          </div>
          <span className="ingestion-card__store" title={run.storeLabel}>
            {run.storeLabel}
          </span>
        </div>
        {canRetry && (
          <button
            type="button"
            className="sm ingestion-card__retry"
            onClick={() => onRetry(run)}
            disabled={pending}
            aria-label={t('ingestion.retry.aria', { store: run.storeLabel })}
          >
            {pending ? t('ingestion.retry.pending') : t('ingestion.retry')}
          </button>
        )}
      </header>

      <dl className="ingestion-card__meta">
        <div className="ingestion-card__meta-item">
          <dt>{t('ingestion.card.when')}</dt>
          <dd className="num">{dateTime(when)}</dd>
        </div>
        <div className="ingestion-card__meta-item">
          <dt>{t('ingestion.card.covered')}</dt>
          <dd className={run.coveredFrom && run.coveredTo ? 'num' : 'muted'}>
            {run.coveredFrom && run.coveredTo
              ? period(run.coveredFrom, run.coveredTo)
              : t('ingestion.covered.none')}
          </dd>
        </div>
        <div className="ingestion-card__meta-item">
          <dt>{t('ingestion.col.trigger')}</dt>
          <dd>{t(`ingestion.trigger.${run.trigger}` as 'ingestion.trigger.scheduled')}</dd>
        </div>
        <div className="ingestion-card__meta-item">
          <dt>{t('ingestion.col.mode')}</dt>
          <dd>{t(`ingestion.mode.${run.mode}` as 'ingestion.mode.daily')}</dd>
        </div>
      </dl>

      {isRunning ? (
        <p className="ingestion-card__note muted" role="status">
          {t('ingestion.running.note')}
        </p>
      ) : run.error ? (
        <p className="ingestion-card__error">{run.error}</p>
      ) : (
        <p className="ingestion-card__advanced muted">
          {run.advanced ? t('ingestion.advanced') : t('ingestion.notAdvanced')}
        </p>
      )}

      {run.failedSources.length > 0 && (
        <p className="ingestion-card__sources muted">
          {t('ingestion.failedSources', { sources: run.failedSources.join(', ') })}
        </p>
      )}

      {!isRunning && (
        <div
          className="ingestion-card__persisted"
          aria-label={t('ingestion.persisted.aria', {
            orders: p.orders,
            statements: p.statements,
            payouts: p.payouts,
            provisioned: p.provisionedItems,
            campaigns: p.campaigns,
            matched: p.ordersMatched,
            rawJson: p.rawJsonCaptured,
          })}
        >
          {anyPersisted ? (
            persistedParts
              .filter((part) => part.value > 0)
              .map((part) => (
                <span key={part.label} className="ingestion-count" aria-hidden="true">
                  <span className="ingestion-count__value">{num(part.value)}</span>
                  <span className="ingestion-count__label">{part.label}</span>
                </span>
              ))
          ) : (
            <span className="muted ingestion-card__nothing" aria-hidden="true">
              {t('ingestion.card.nothing')}
            </span>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The lifecycle badge. State is spelled out in text (not colour alone — WCAG AA)
 * and the colour comes from the shared state tokens. A `running` badge pulses its
 * dot so an in-flight run is unmistakable even at a glance.
 */
function StatusBadge({ status }: { status: IngestionRun['status'] }) {
  const { t } = useI18n();
  const label = t(`ingestion.status.${status}` as 'ingestion.status.running');
  return (
    <span
      className={`badge ingestion-badge ingestion-badge--${status}`}
      aria-label={t('ingestion.status.aria', { label })}
    >
      {label}
    </span>
  );
}

/**
 * The platform the run pulled from (Talabat / Careem), badged so the admin reads
 * the source at a glance. A known platform gets its brand tint; anything else
 * (future platforms) renders neutrally with its raw key capitalized.
 */
function PlatformBadge({ platformKey }: { platformKey: string }) {
  const known = platformKey === 'talabat' || platformKey === 'careem';
  const label = platformKey.charAt(0).toUpperCase() + platformKey.slice(1);
  return (
    <span className={`badge ingestion-platform ingestion-platform--${known ? platformKey : 'other'}`}>
      {label}
    </span>
  );
}
